/**
 * RYA Chart 3 TSS overlay — the AUTHORED-FROM-CHART traffic separation scheme.
 *
 * The RYA training ENC cells carry no S-57 traffic-separation objects (TSSLPT,
 * TSEZNE, …), so the whole TSS the paper chart shows around the Lawrence Channel
 * junction is missing from the vector: the Lawrence Channel zone, the Farlow
 * Channel zone, the SE approach, their lanes, and the precautionary area. This
 * overlay fills that gap from a hand-transcribed data file
 * (public/charts/authored/tss-chart3.local.geojson), digitised off the RYA Chart 3
 * raster scan. It is NOT ENC data: the zones were auto-fit (PCA) to the magenta
 * fills; the lanes, limits and traffic directions were read from the chart.
 * Treat positions as provisional until verified. The file is gitignored (.local),
 * so this overlay simply no-ops where the file isn't present.
 *
 * Rendering reuses the fairway/traffic look: magenta zone fill + outline, dashed
 * magenta limit lines, and the same `chart-traffic-arrow` glyph for the lane flow,
 * rotated to each lane's transcribed direction. Shown from low zoom, like the real
 * fairways, because a TSS is a large-area navigational feature.
 */
import type { Map as MaplibreMap } from 'maplibre-gl';
import type { Palette } from '@/store';
import { getPalette } from './style/palettes';

const SRC = 'tss-lawrence';
const URL_PATH = 'charts/authored/tss-chart3.local.geojson';

/** Layer ids share the `tss-` prefix so the raster-mode hide sweep can find them. */
const LAYER_IDS = ['tss-itz', 'tss-zone-fill', 'tss-zone-line', 'tss-limit', 'tss-lane-arrow', 'tss-precautionary'] as const;

function url(): string {
  const base = import.meta.env.BASE_URL ?? '/';
  return `${base}${URL_PATH}`;
}

/**
 * Add (or recolour) the authored TSS overlay. Safe to call repeatedly: it fetches
 * once, adds the source + layers if missing, and refreshes the magenta to the
 * current palette on later calls (e.g. a day/night swap). No file, no overlay.
 */
export async function ensureTssOverlay(map: MaplibreMap, palette: Palette): Promise<void> {
  const mag = getPalette(palette).CHMGD;

  // Already mounted: just refresh the palette-driven colours.
  if (map.getSource(SRC)) {
    if (map.getLayer('tss-itz')) map.setPaintProperty('tss-itz', 'fill-color', mag);
    if (map.getLayer('tss-zone-fill')) map.setPaintProperty('tss-zone-fill', 'fill-color', mag);
    if (map.getLayer('tss-zone-line')) map.setPaintProperty('tss-zone-line', 'line-color', mag);
    if (map.getLayer('tss-limit')) map.setPaintProperty('tss-limit', 'line-color', mag);
    if (map.getLayer('tss-precautionary')) map.setPaintProperty('tss-precautionary', 'circle-stroke-color', mag);
    return;
  }

  let data: GeoJSON.FeatureCollection;
  try {
    const r = await fetch(url());
    if (!r.ok) return; // No authored file in this environment: nothing to draw.
    data = (await r.json()) as GeoJSON.FeatureCollection;
  } catch {
    return;
  }
  if (map.getSource(SRC)) return; // A concurrent call won the race.

  map.addSource(SRC, { type: 'geojson', data });

  // Inshore Traffic Zones: a faint magenta wash, lighter than the separation zone,
  // sitting beneath everything else. The true outer edge is the coast; the band is
  // a nominal width (see the feature note), so this reads as "ITZ here", not a
  // hard boundary.
  map.addLayer({
    id: 'tss-itz',
    type: 'fill',
    source: SRC,
    filter: ['==', ['get', 'kind'], 'itz'],
    minzoom: 7,
    paint: { 'fill-color': mag, 'fill-opacity': 0.1 },
  });
  map.addLayer({
    id: 'tss-zone-fill',
    type: 'fill',
    source: SRC,
    filter: ['==', ['get', 'kind'], 'separation_zone'],
    minzoom: 7,
    paint: { 'fill-color': mag, 'fill-opacity': 0.3 },
  });
  map.addLayer({
    id: 'tss-zone-line',
    type: 'line',
    source: SRC,
    filter: ['==', ['get', 'kind'], 'separation_zone'],
    minzoom: 7,
    paint: { 'line-color': mag, 'line-width': 1.4, 'line-opacity': 0.9 },
  });
  map.addLayer({
    id: 'tss-limit',
    type: 'line',
    source: SRC,
    filter: ['==', ['get', 'kind'], 'limit'],
    minzoom: 7,
    paint: { 'line-color': mag, 'line-width': 1, 'line-dasharray': [3, 2], 'line-opacity': 0.8 },
  });
  map.addLayer({
    id: 'tss-lane-arrow',
    type: 'symbol',
    source: SRC,
    filter: ['==', ['get', 'kind'], 'lane'],
    minzoom: 7,
    layout: {
      'icon-image': 'chart-traffic-arrow',
      'icon-rotate': ['to-number', ['coalesce', ['get', 'dir_deg'], 0]],
      'icon-rotation-alignment': 'map',
      'icon-size': ['interpolate', ['linear'], ['zoom'], 7, 0.45, 11, 0.7, 14, 0.95],
      'icon-allow-overlap': true,
      'icon-ignore-placement': true,
    },
  });
  // Precautionary area (the caution-triangle junction): a magenta ring marker.
  map.addLayer({
    id: 'tss-precautionary',
    type: 'circle',
    source: SRC,
    filter: ['==', ['get', 'kind'], 'precautionary_area'],
    minzoom: 7,
    paint: {
      'circle-radius': ['interpolate', ['linear'], ['zoom'], 7, 4, 12, 11],
      'circle-color': 'rgba(0,0,0,0)',
      'circle-stroke-color': mag,
      'circle-stroke-width': 2,
    },
  });
}

/** Remove the overlay (source + layers). */
export function removeTssOverlay(map: MaplibreMap): void {
  for (const id of LAYER_IDS) if (map.getLayer(id)) map.removeLayer(id);
  if (map.getSource(SRC)) map.removeSource(SRC);
}
